// Comms final pass (1.12.217-beta): pull to refresh on touch devices (phones AND tablets, any coarse pointer, iPad included).
//
// Pulling down on a scroll pane that is already at its very top reloads that pane's data in place (the caller's reload function;
// never a page reload). It never fights normal scrolling:
//   * it only starts when the pane's scrollTop is 0 at touchstart and the finger moves DOWN, more vertically than sideways;
//   * once the pane has scrolled at all, or the finger goes up, the gesture belongs to the browser — including mid-pull: if the
//     finger turns back up (more than REVERSE_SLOP_PX above the lowest point it reached), the pull is dropped and touchmove is no
//     longer prevented, so the person can scroll the list again in the same touch (Comms gate fixes);
//   * only then is touchmove's default prevented (so iOS / iPadOS Safari and the installed home-screen app don't rubber-band or
//     run their own pull, and Chrome's page-level pull-to-refresh can't reload the whole app).
// Mouse and pen users never fire touch events, so desktop is unaffected; the hook is also switched off when the device reports
// no coarse pointer and no touch points.
import { useCallback, useEffect, useRef, useState } from 'react';

export const PULL_THRESHOLD_PX = 64;
export const PULL_MAX_PX = 96;
const START_SLOP_PX = 6;
/** Finger travel back up (from the lowest point of the pull) that ends the pull and gives the touch back to the browser. */
export const REVERSE_SLOP_PX = 12;

/** Finger travel → indicator height: half speed (it feels attached), capped. Pure, for tests. */
export function pullDistanceFor(deltaY) {
    const dy = Number(deltaY) || 0;
    if (dy <= 0) return 0;
    return Math.min(PULL_MAX_PX, Math.round(dy * 0.5));
}

/** 'idle' | 'pulling' | 'armed' (release now refreshes) | 'refreshing'. Pure, for tests. */
export function pullPhaseFor(distance, refreshing = false) {
    if (refreshing) return 'refreshing';
    if (distance >= PULL_THRESHOLD_PX) return 'armed';
    return distance > 0 ? 'pulling' : 'idle';
}

/** Any touch-capable device: a coarse pointer anywhere (phones, iPad with or without a keyboard) or reported touch points. */
export function isTouchCapable(win = typeof window !== 'undefined' ? window : undefined) {
    if (!win) return false;
    try {
        if (typeof win.matchMedia === 'function'
            && (win.matchMedia('(pointer: coarse)').matches || win.matchMedia('(any-pointer: coarse)').matches)) return true;
    } catch { /* fall through */ }
    return Number(win.navigator?.maxTouchPoints) > 0;
}

export function prefersReducedMotion(win = typeof window !== 'undefined' ? window : undefined) {
    try {
        return Boolean(win?.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
    } catch {
        return false;
    }
}

export default function usePullToRefresh(scrollRef, { onRefresh, enabled = true } = {}) {
    const [distance, setDistance] = useState(0);
    const [refreshing, setRefreshing] = useState(false);
    const gestureRef = useRef(null);
    const distanceRef = useRef(0);
    const refreshingRef = useRef(false);
    const onRefreshRef = useRef(onRefresh);
    onRefreshRef.current = onRefresh;
    const [touchCapable, setTouchCapable] = useState(() => isTouchCapable());

    useEffect(() => {
        setTouchCapable(isTouchCapable());
    }, []);

    const run = useCallback(async () => {
        if (refreshingRef.current || typeof onRefreshRef.current !== 'function') return;
        refreshingRef.current = true;
        setRefreshing(true);
        try {
            await onRefreshRef.current();
        } catch { /* the pane's own error handling shows a failed load */ } finally {
            refreshingRef.current = false;
            distanceRef.current = 0;
            setRefreshing(false);
            setDistance(0);
        }
    }, []);

    useEffect(() => {
        const node = scrollRef?.current;
        if (!enabled || !touchCapable || !node || typeof node.addEventListener !== 'function') return undefined;
        const onStart = (event) => {
            if (refreshingRef.current || event.touches?.length !== 1 || node.scrollTop > 0) {
                gestureRef.current = null;
                return;
            }
            const touch = event.touches[0];
            gestureRef.current = { x: touch.clientX, y: touch.clientY, pulling: false, decided: false, maxDy: 0 };
        };
        const onMove = (event) => {
            const gesture = gestureRef.current;
            if (!gesture) return;
            const touch = event.touches?.[0];
            if (!touch) return;
            const dy = touch.clientY - gesture.y;
            const dx = touch.clientX - gesture.x;
            if (!gesture.decided) {
                if (Math.abs(dy) < START_SLOP_PX && Math.abs(dx) < START_SLOP_PX) return;
                gesture.decided = true;
                // up, sideways, or the pane has moved: an ordinary scroll, not ours
                if (dy <= 0 || Math.abs(dx) > Math.abs(dy) || node.scrollTop > 0) {
                    gestureRef.current = null;
                    return;
                }
                gesture.pulling = true;
            }
            if (!gesture.pulling) return;
            // the finger turned back up: not a pull any more. Stop preventing the default so the browser scrolls normally.
            if (dy <= 0 || dy < gesture.maxDy - REVERSE_SLOP_PX) {
                gestureRef.current = null;
                distanceRef.current = 0;
                setDistance(0);
                return;
            }
            gesture.maxDy = Math.max(gesture.maxDy, dy);
            if (node.scrollTop > 0) {
                gestureRef.current = null;
                distanceRef.current = 0;
                setDistance(0);
                return;
            }
            if (event.cancelable) event.preventDefault();
            distanceRef.current = pullDistanceFor(dy);
            setDistance(distanceRef.current);
        };
        const onEnd = () => {
            const gesture = gestureRef.current;
            gestureRef.current = null;
            if (!gesture?.pulling) return;
            const pulled = distanceRef.current;
            distanceRef.current = 0;
            if (pulled >= PULL_THRESHOLD_PX) {
                setDistance(PULL_THRESHOLD_PX);
                run();
            } else {
                setDistance(0);
            }
        };
        node.addEventListener('touchstart', onStart, { passive: true });
        node.addEventListener('touchmove', onMove, { passive: false });
        node.addEventListener('touchend', onEnd, { passive: true });
        node.addEventListener('touchcancel', onEnd, { passive: true });
        return () => {
            node.removeEventListener('touchstart', onStart);
            node.removeEventListener('touchmove', onMove);
            node.removeEventListener('touchend', onEnd);
            node.removeEventListener('touchcancel', onEnd);
        };
    }, [enabled, run, scrollRef, touchCapable]);

    return {
        active: Boolean(enabled && touchCapable),
        distance: refreshing ? PULL_THRESHOLD_PX : distance,
        phase: pullPhaseFor(distance, refreshing),
        refreshing,
        refresh: run,
    };
}
